export interface WebRTCOptions {
  onTrack?: (event: RTCTrackEvent) => void;
  onDataChannel?: (channel: RTCDataChannel) => void;
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
  onIceCandidate?: (candidate: RTCIceCandidate) => void;
  iceServers?: RTCIceServer[];
}

export abstract class WebRTCClient {
  protected peerConnection: RTCPeerConnection | null = null;
  protected dataChannels = new Map<string, RTCDataChannel>();
  protected iceServers: RTCIceServer[];

  constructor(iceServers?: RTCIceServer[]) {
    this.iceServers = iceServers || [
      { urls: "stun:stun.l.google.com:19302" },
    ];
  }

  async createConnection(options: WebRTCOptions = {}): Promise<RTCPeerConnection> {
    const config: RTCConfiguration = {
      iceServers: this.iceServers,
    };

    this.peerConnection = new RTCPeerConnection(config);

    if (options.onTrack) {
      this.peerConnection.ontrack = options.onTrack;
    }

    if (options.onDataChannel) {
      this.peerConnection.ondatachannel = (event) => {
        options.onDataChannel?.(event.channel);
      };
    }

    if (options.onConnectionStateChange) {
      this.peerConnection.onconnectionstatechange = () => {
        options.onConnectionStateChange?.(this.peerConnection!.connectionState);
      };
    }

    if (options.onIceCandidate) {
      this.peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          options.onIceCandidate?.(event.candidate);
        }
      };
    }

    return this.peerConnection;
  }

  async createOffer(): Promise<RTCSessionDescriptionInit> {
    if (!this.peerConnection) {
      throw new Error("Connection not created. Call createConnection first.");
    }

    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    return offer;
  }

  async createAnswer(offer: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> {
    if (!this.peerConnection) {
      throw new Error("Connection not created. Call createConnection first.");
    }

    await this.peerConnection.setRemoteDescription(offer);
    const answer = await this.peerConnection.createAnswer();
    await this.peerConnection.setLocalDescription(answer);
    return answer;
  }

  async setRemoteDescription(description: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peerConnection) {
      throw new Error("Connection not created. Call createConnection first.");
    }
    await this.peerConnection.setRemoteDescription(description);
  }

  async addIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!this.peerConnection) {
      throw new Error("Connection not created. Call createConnection first.");
    }
    await this.peerConnection.addIceCandidate(candidate);
  }

  createDataChannel(label: string, options?: RTCDataChannelInit): RTCDataChannel {
    if (!this.peerConnection) {
      throw new Error("Connection not created. Call createConnection first.");
    }

    const channel = this.peerConnection.createDataChannel(label, options);
    this.dataChannels.set(label, channel);
    return channel;
  }

  getDataChannel(label: string): RTCDataChannel | undefined {
    return this.dataChannels.get(label);
  }

  close(): void {
    this.dataChannels.forEach((channel) => channel.close());
    this.dataChannels.clear();
    this.peerConnection?.close();
    this.peerConnection = null;
  }

  getConnectionState(): RTCPeerConnectionState | null {
    return this.peerConnection?.connectionState || null;
  }
}

